"use client";

import { Fragment, useEffect, useRef, useState, type RefObject } from "react";
import { projects, type ProjectProps } from "../work/projectDetails";
import {
  palettes,
  usePortfolioTheme,
  usePortfolioThemeActions,
} from "./ThemeStudio";
import { usePortfolioConsole } from "./console-context";
import TerminalTextLine from "./TerminalTextLine";
import { fileContents, portfolioFileNames, findPortfolioFile, type PortfolioFile } from "./portfolio-files-data";
import TerminalBoot, { terminalOutput, type BootKind } from "./TerminalBoot";

type Entry = {
  prompt: string;
  command: string;
  output: string;
  links?: { label: string; href: string; download?: boolean }[];
};
const email = "contact@sahiya.lk";
const resume = "/Sahindu-Gayanuka-Resume.pdf";
const profile =
  "Sahindu Gayanuka / Sri Lanka\nFull stack developer. BSc (Hons) Computer Security, Plymouth University — 2:1.\nTheDBot: one year in full stack development.\nAssembleX: founder & developer, building websites, apps, and cloud integrations.";
const help = `A FEW CLUES
  Every home directory has a story. Have a look around.
  Some files are for reading. Others are meant to be run.
  This shell has another side, if you can get it running.

  Start typing. Tab can fill in the rest.

PORTFOLIO COMMANDS
  ls / cd projects / cd ..       Explore the workspace
  files                         Open the file explorer
  cat about.txt                 Meet Sahindu
  portfolio projects --stack react    Find matching projects
  portfolio project open aiden        Open a case study
  goto about                    Jump to a section
  portfolio contact --copy            Copy the email address
  portfolio resume                   Preview or download the PDF
  portfolio theme set aqua            Change the site accent
  portfolio status                   Inspect this workspace

SYSTEM COMMANDS
  ssh guest@sahiya.lk            Connect to the portfolio host
  ./install.sh                  Run the workspace installer
  sudo apt install portfolio-desktop  Install the desktop package
  startx                        Start the installed desktop
  cat install.sh                Read the installer
  pwd / whoami / help           Inspect the shell and commands

PROJECTS
  portfolio projects            List all projects
  portfolio projects open <name or number>  Open a case study
  portfolio project open aiden   Open by name (partial names work)
  portfolio projects open 01     Open by displayed project number

LITTLE DISCOVERIES
  coffee / cat notebook.txt / github

↑ ↓ history   Tab complete   clear   exit`;
const completions = [
  "./install.sh",
  "bash install.sh",
  "ssh guest@sahiya.lk",
  "sudo apt install portfolio-desktop",
  "help",
  "ls",
  "ls -a",
  "ls -la",
  ...portfolioFileNames.map(file => "cat " + file),
  "files",
  "open files",
  "cd projects",
  "cd ..",
  "cat about.txt",
  "cat notebook.txt",
  "cat install.sh",
  "pwd",
  "whoami",
  "portfolio projects",
  "portfolio projects open ",
  "portfolio projects --stack react",
  "portfolio project open ",
  "portfolio contact --copy",
  "portfolio resume",
  "portfolio theme set ",
  "portfolio status",
  "startx",
  "portfolio ui status",
  "render ui",
  "goto about",
  "goto experience",
  "goto contact",
  "coffee",
  "github",
  "clear",
  "exit",
];

export default function LegacyTerminal({
  inputRef,
  active,
  desktopInstalled,
  desktopRunning,
  request,
  onRender,
  onClose,
  onVisit,
  onProject,
  findProjects,
  onFile,
  onGithub,
}: {
  inputRef: RefObject<HTMLInputElement>;
  active: boolean;
  desktopInstalled: boolean;
  desktopRunning: boolean;
  request: { screen: string } | null;
  onRender: (ready?: boolean) => void;
  onClose: () => void;
  onVisit: (section: string) => void;
  onProject: (project: ProjectProps) => void;
  findProjects: (query: string) => ProjectProps[];
  onFile: (file: PortfolioFile | null) => void;
  onGithub: (url?: string) => void;
}) {
  const palette = usePortfolioTheme();
  const { choosePalette } = usePortfolioThemeActions();
  const { coffeeLifted, toggleCoffee } = usePortfolioConsole();
  const [line, setLine] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [job, setJob] = useState<BootKind | null>(null);
  const [installed, setInstalled] = useState(desktopInstalled);
  const [connected, setConnected] = useState(false);
  const previousDesktopRunning = useRef(desktopRunning);
  const [directory, setDirectory] = useState("~");
  const [entries, setEntries] = useState<Entry[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [completionIndex, setCompletionIndex] = useState(0);
  const draft = useRef("");
  const log = useRef<HTMLDivElement>(null);
  const seenRequest = useRef<typeof request>();
  const write = (command: string, output: string, links?: Entry["links"]) =>
    setEntries((previous) => [
      ...previous.slice(-59),
      { command, output, links, prompt: (connected ? "guest@sahiya.lk" : "visitor@localhost") + ":" + directory + "$" },
    ]);
  const listProjects = (query: string) => {
    const matches = findProjects(query);
    return matches.length
      ? matches
          .map(
            (project) =>
              String(project.id + 1).padStart(2, "0") +
              "  " +
              project.name +
              "\n    " +
              project.techNames.join(" / ")
          )
          .join("\n\n") +
          "\n\nOpen a case study: portfolio project open <name or number>"
      : "No projects matched. Try react, python, security, or flutter.";
  };
  const execute = async (raw: string) => {
    const command = raw.trim();
    if (!command) return;
    setLine("");
    setHistoryIndex(-1);
    setHistory((previous) =>
      [command, ...previous.filter((item) => item !== command)].slice(0, 40)
    );
    const value = command.toLowerCase().replace(/^(sahi|sahindu|portfolio)\s+/, "");
    if (["help", "--help", "sahi"].includes(value)) {
      write(command, help); return;
    }
    if (value === "clear" || value === "cls") { setEntries([]); return; }
    if (value === "exit" || value === "quit") {
      if (connected) { setConnected(false); write(command, "logout\nConnection to sahiya.lk closed."); }
      else onClose();
      return;
    }
    if (value === "ssh guest@sahiya.lk") {
      if (connected) write(command, "Already connected to sahiya.lk.");
      else setJob("ssh");
      return;
    }
    if (value.startsWith("ssh ")) {
      write(command, "ssh: Could not resolve hostname.\nAvailable host: guest@sahiya.lk"); return;
    }
    if (!connected) {
      write(command, "No active remote session. Connect with ssh guest@sahiya.lk"); return;
    }
    if (["./install.sh", "bash install.sh", "sh install.sh", "install.sh"].includes(value)) {
      if (installed) write(command, "portfolio-desktop is already installed.\nRun startx to start your session.");
      else setJob("script");
      return;
    }
    if (/^(sudo )?apt(-get)? install(?: --(?:yes|no-install-recommends))* portfolio-desktop$/.test(value)) {
      if (installed) write(command, "portfolio-desktop is already the newest version (1.0.0).\nRun startx to launch it.");
      else setJob("install");
      return;
    }
    if (
      [
        "startx",
        "launch",
        "open workspace",
        "ui start",
        "ui build",
        "render ui",
        "render",
        "start ui",
        "launch ui",
      ].includes(value)
    ) {
      if (!installed) {
        write(command, "startx: portfolio-desktop is not installed.");
        return;
      }
      write(command, desktopRunning ? "Display :0 is already running. Restoring workspace..." : "Starting graphical session...");
      onRender();
      return;
    }
    if (value === "clear" || value === "cls") {
      setEntries([]);
      return;
    }
    if (value === "exit" || value === "quit") {
      onClose();
      return;
    }
    if (value === "pwd") {
      write(command, directory === "~" ? "/portfolio" : "/portfolio/projects");
      return;
    }
    if (
      value === "cd" ||
      value === "cd .." ||
      value === "cd ~" ||
      value === "cd /"
    ) {
      setDirectory("~");
      write(command, "/portfolio");
      return;
    }
    if (value === "cd projects" || value === "cd projects/") {
      setDirectory("~/projects");
      write(command, listProjects(""));
      return;
    }
    if (value.startsWith("cd ")) {
      write(command, "Directory not found. Try cd projects or cd ..");
      return;
    }
    if (value === "files" || value === "open files") { onFile(null); write(command,"Opening home directory..."); return; }
    if (value === "ls" || value === "dir" || value === "ls -a" || value === "ls -la" || value === "ls -l") {
      if (directory !== "~") {write(command,listProjects(""));return;}
      const flags = value.startsWith("ls ") ? value.slice(3) : "";
      const names = portfolioFileNames.filter(file=>flags.includes("a") || !file.startsWith("."));
      const output = flags.includes("l")
        ? ["drwxr-xr-x  guest guest       -  projects/", ...names.map(file=>{
          const bytes = file === "resume.pdf" ? 98931 : new TextEncoder().encode(fileContents[file]).length;
          const mode = file === "install.sh" ? "-rwxr-xr-x" : "-rw-r--r--";
          return `${mode}  guest guest  ${String(bytes).padStart(6)}  ${file}`;
        })].join("\n")
        : [...(flags.includes("a") ? [".",".."] : []),"projects/",...names.map(file=>file==="install.sh"?file+"*":file)].join("\n");
      write(command,output);return;
    }
    if (value === "status" || value === "ui status") {
      write(
        command,
        value === "ui status"
          ? "interface  text mode\nworkspace  ready to assemble\nstart      startx"
          : "workspace  Sahindu’s portfolio\nprojects   " +
              projects.length +
              "\ninterface  text mode\naccent     " +
              palette.name +
              "\nemail      " +
              email
      );
      return;
    }
    if (value.startsWith("cat ")) {
      const name = value.slice(4).trim();
      const file = findPortfolioFile(name);
      if (!file) {write(command,`cat: ${name}: No such file or directory`);return;}
      onFile(file);
      write(command, file === "resume.pdf" ? "Opening resume.pdf..." : fileContents[file]);
      return;
    }
    if (["about", "whoami", "experience"].includes(value)) {
      write(command, profile);
      return;
    }
    if (value === "notebook") {
      onFile("notebook.txt"); write(command,fileContents["notebook.txt"]); return;
    }
    if (/^(projects?|skills?)(\s|$)/.test(value) && !/^projects? open(?:\s|$)/.test(value)) {
      const filter = value
        .replace(/^(projects?|skills?)\s*/, "")
        .replace(/^--stack\s+/, "");
      write(command, listProjects(filter));
      return;
    }
    if (/^(projects? open|open)\s+/.test(value)) {
      const target = value.replace(/^(projects? open|open)\s+/, "");
      if (["resume", "resume.pdf", "résumé"].includes(target)) {
        onFile("resume.pdf");
        write(command, "Sahindu Gayanuka / Résumé", [
          { label: "Open résumé.pdf ↗", href: resume },
          { label: "Download PDF ↓", href: resume, download: true },
        ]);
        return;
      }
      const targetVariants = [target, ...Array.from({ length: Math.max(0, target.length - 1) }, (_, index) =>
        target.slice(0, index) + target[index + 1] + target[index] + target.slice(index + 2)
      )];
      const directMatches = projects.filter(project => project.name.toLowerCase().includes(target));
      const matches = /^\d+$/.test(target)
        ? projects.filter((project) => project.id === Number(target) - 1)
        : directMatches.length ? directMatches : projects.filter(project =>
            targetVariants.some(variant => project.name.toLowerCase() === variant)
          );
      if (matches.length === 1) onProject(matches[0]);
      else
        write(
          command,
          matches.length
            ? "More than one match. Use the full project name.\n" +
                matches.map((project) => project.name).join("\n")
            : "Project not found. Run portfolio projects to see the work."
        );
      return;
    }
    if (value.startsWith("goto ") || value === "desk") {
      const section = value === "desk" ? "experience" : value.slice(5).trim();
      if (["home", "work", "about", "experience", "contact"].includes(section))
        onVisit(section);
      else
        write(
          command,
          "Available sections: home, work, about, experience, contact."
        );
      return;
    }
    if (["copy email", "contact --copy", "email --copy"].includes(value)) {
      try {
        await navigator.clipboard.writeText(email);
        write(command, "Copied " + email);
      } catch {
        write(command, "Copy this address: " + email);
      }
      return;
    }
    if (value === "contact" || value === "email") {
      write(command, email + "\nCopy it: portfolio contact --copy", [
        { label: "Compose an email ↗", href: "mailto:" + email },
      ]);
      return;
    }
    if (
      value === "resume" ||
      value === "résumé" ||
      value === "cat resume.pdf"
    ) {
      onFile("resume.pdf");
      write(command, "Sahindu Gayanuka / Résumé / PDF", [
        { label: "Open résumé.pdf ↗", href: resume },
        { label: "Download PDF ↓", href: resume, download: true },
      ]);
      return;
    }
    if (value === "github") {
      onGithub();
      write(command, "Source code & experiments", [
        {
          label: "github.com/SahiDemon ↗",
          href: "https://github.com/SahiDemon",
        },
      ]);
      return;
    }
    if (value === "theme") {
      write(
        command,
        "Current accent: " +
          palette.name +
          "\nAvailable: " +
          palettes.map((option) => option.id).join(", ") +
          "\nSet one: portfolio theme set aqua"
      );
      return;
    }
    if (value.startsWith("theme ")) {
      const name = value.replace(/^theme\s+(set\s+)?/, "");
      const choice = palettes.find(
        (option) => option.id === name || option.name.toLowerCase() === name
      );
      if (choice) {
        choosePalette(choice);
        write(
          command,
          "Accent set to " + choice.name + ". The portfolio changed with it."
        );
      } else
        write(
          command,
          "Unknown accent. Run theme to list the available colours."
        );
      return;
    }
    if (value === "coffee") {
      toggleCoffee();
      write(
        command,
        coffeeLifted
          ? "Cup down. Back to building."
          : "Cup lifted. A coffee ring remains on the desk."
      );
      return;
    }
    write(
      command,
      "Command not found: " +
        command +
        "\nType help for commands, or startx for the visual workspace."
    );
  };
  const run = (raw: string) => {
    const command = raw.trim();
    if (!command || pendingTimer.current !== null || job) return;
    setLine("");
    setPending(command);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const installer = /^(?:\.\/|bash |sh )?install\.sh$|^(?:sudo )?apt(?:-get)? install /.test(command.toLowerCase());
    const delay = reduced ? 0 : installer
      ? 450 + Math.random() * 450
      : /^(ssh |startx)/.test(command.toLowerCase())
        ? 180 + Math.random() * 240
        : 90 + Math.random() * 220;
    pendingTimer.current = setTimeout(() => {
      pendingTimer.current = null;
      setPending(null);
      void execute(command);
    }, delay);
  };
  useEffect(() => () => {
    if (pendingTimer.current !== null) clearTimeout(pendingTimer.current);
  }, []);
  useEffect(() => {
    if (request === seenRequest.current) return;
    seenRequest.current = request;
    if (request && request.screen !== "search")
      void run(request.screen === "project" ? "projects" : request.screen);
  }, [request]); // Requests are explicit actions from the desk or launcher.
  useEffect(() => {
    if (log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [entries, line]);

  useEffect(() => {
    if (previousDesktopRunning.current && !desktopRunning) {
      write("", "[1]  Done  startx\nGraphical session ended.");
    } else if (!previousDesktopRunning.current && desktopRunning) {
      write("", "[1]  Running  startx\nDisplay :0 ready.");
    }
    previousDesktopRunning.current = desktopRunning;
  }, [desktopRunning]);

  useEffect(() => {
    // Activating a window must not steal focus from its close/minimize controls.
    const focused = document.activeElement;
    const usingControl = focused instanceof Element && focused.closest("button, .floating-workspace");
    if (!job && active && !usingControl) inputRef.current?.focus({ preventScroll: true });
  }, [job, inputRef, active]);

  if (job) return <TerminalBoot kind={job} onCancel={() => {
    write(job === "script" ? "./install.sh" : job === "ssh" ? "ssh guest@sahiya.lk" : "sudo apt install portfolio-desktop", "^C\nSession interrupted.");
    setJob(null);
  }} onDone={() => {
    if (job === "install" || job === "script") {
      setInstalled(true);
      write(job === "script" ? "./install.sh" : "sudo apt install portfolio-desktop", terminalOutput(job).join("\n"));
    } else {
      setConnected(true);
      write("ssh guest@sahiya.lk", terminalOutput("ssh").join("\n"));
    }
    setJob(null);
  }} />;

  const projectCommands = projects.flatMap(project =>
    ["portfolio projects open", "portfolio project open", "open"].flatMap(prefix =>
      [project.name.toLowerCase(), String(project.id + 1), String(project.id + 1).padStart(2, "0")]
        .map(target => `${prefix} ${target}`)
    )
  );
  const availableCommands = connected
    ? [...completions, ...projectCommands, ...palettes.map(option => `portfolio theme set ${option.id}`)]
    : ["ssh guest@sahiya.lk", "help", "clear", "exit"];
  const candidates = line.trim() && !pending
    ? [...new Set([...history, ...availableCommands])].filter(command =>
        command.toLowerCase().startsWith(line.toLowerCase()) && command.trimEnd() !== line.trimEnd().toLowerCase()
      ).slice(0, 6)
    : [];
  const selectedCompletion = Math.min(completionIndex, Math.max(0, candidates.length - 1));
  const completeWord = (candidate: string) => {
    const remaining = candidate.slice(line.length);
    // Accept just the next shell word, keeping the rest for later Tab presses.
    const nextWord = remaining.match(/^\s*\S+\s*/)?.[0] ?? remaining;
    return line + nextWord + (line.length + nextWord.length === candidate.length && !/\s$/.test(nextWord) ? " " : "");
  };
  const suggestion = candidates[selectedCompletion] ? completeWord(candidates[selectedCompletion]) : "";

  return (
    <section className="legacy-terminal" aria-label="Portfolio text terminal"
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("a, button, input, textarea, select, label")) return;
        if (window.getSelection()?.toString()) return;
        inputRef.current?.focus({ preventScroll: true });
      }}>
      <div className="legacy-transcript" ref={log}>
        <div className="legacy-welcome">
          <pre className="terminal-wordmark" aria-label="SAHIYA">{" ____    _    _   _ _____   __    _\n/ ___|  / \\  | | | |_ _\\ \\ / /   / \\\n\\___ \\ / _ \\ | |_| || | \\ V /   / _ \\\n ___) / ___ \\|  _  || |  | |   / ___ \\\n|____/_/   \\_\\_| |_|___| |_|  /_/   \\_\\"}</pre>
          <p>Portfolio shell</p>
          <p>Remote host: <strong>sahiya.lk</strong><br />
          Login: <strong>guest</strong></p>
          <p>Open a session with <strong>ssh guest@sahiya.lk</strong><br />
          Type <strong>help</strong> for assistance.</p>
        </div>
        <div
          className="legacy-entries"
          role="log"
          aria-live="polite"
          aria-relevant="additions"
        >
          {entries.map((entry, index) => (
            <div className="legacy-entry" key={index}>
              {entry.command && <p className="legacy-echo">
                <span>{entry.prompt}</span> {entry.command}
              </p>}
              <pre>{entry.output.split("\n").map((line,index,lines)=><Fragment key={index}><TerminalTextLine line={line} />{index<lines.length-1?"\n":null}</Fragment>)}</pre>
              {entry.links?.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  download={link.download || undefined}
                  target={
                    link.download || link.href.startsWith("mailto:")
                      ? undefined
                      : "_blank"
                  }
                  rel="noreferrer"
                  onClick={event => {
                    if (link.download) return;
                    if (link.href === resume) {event.preventDefault();onFile("resume.pdf");}
                    else if (link.href.startsWith("https://github.com/")) {event.preventDefault();onGithub(link.href);}
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          ))}
        </div>
      <form
        className="legacy-prompt"
        aria-busy={pending !== null}
        onSubmit={(event) => {
          event.preventDefault();
          void run(line);
        }}
      >
        <label htmlFor="sahi-terminal-command">
          <span>{connected ? "guest@sahiya.lk" : "visitor@localhost"}</span>:<em>{directory}</em>
          <b>$</b>
        </label>
        <div className="terminal-input-wrap">
        <span className="terminal-ghost" aria-hidden="true"><span>{line}</span>{pending ? "" : suggestion.slice(line.length)}</span>
        <input
          id="sahi-terminal-command"
          ref={inputRef}
          aria-label="Terminal command"
          value={pending ?? line}
          readOnly={pending !== null}
          onChange={(event) => {
            setLine(event.target.value);
            setHistoryIndex(-1);
            setCompletionIndex(0);
          }}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-description="Tab completes one word. Arrow keys choose suggestions while typing or browse history on an empty prompt."
          onKeyDown={(event) => {
            if (event.ctrlKey && event.key.toLowerCase() === "c") {
              event.preventDefault();
              if (pendingTimer.current !== null) clearTimeout(pendingTimer.current);
              pendingTimer.current = null;
              write(pending ?? line, "^C");
              setPending(null);
              setLine("");
              return;
            }
            if (pending !== null) return;
            if (candidates.length && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
              event.preventDefault();
              setCompletionIndex((selectedCompletion + (event.key === "ArrowDown" ? 1 : -1) + candidates.length) % candidates.length);
              return;
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              if (!history.length) return;
              if (historyIndex === -1) draft.current = line;
              const next = Math.min(historyIndex + 1, history.length - 1);
              setHistoryIndex(next);
              setLine(history[next]);
            }
            if (event.key === "ArrowDown") {
              event.preventDefault();
              const next = Math.max(-1, historyIndex - 1);
              setHistoryIndex(next);
              setLine(next === -1 ? draft.current : history[next]);
            }
            if (event.key === "Tab" && !event.shiftKey) {
              if (suggestion) {
                event.preventDefault();
                setLine(suggestion);
                setCompletionIndex(0);
              }
            }
          }}
        />
        </div>
        <button type="submit" aria-label="Run terminal command">
          ↵
        </button>
      </form>
      {candidates.length > 0 && (
        <div className="terminal-completions" aria-label="Command suggestions">
          {candidates.map((candidate, index) => (
            <button type="button" key={candidate}
              className={index === selectedCompletion ? "is-selected" : ""}
              aria-label={`Complete next word: ${candidate}`}
              onMouseDown={event => event.preventDefault()}
              onClick={() => { setLine(completeWord(candidate)); setCompletionIndex(0); inputRef.current?.focus(); }}>
              <span><span>{candidate.slice(0, line.length)}</span><span className="completion-rest">{candidate.slice(line.length)}</span></span>
              <small>{history.includes(candidate) ? "history" : "tab"}</small>
            </button>
          ))}
        </div>
      )}
      </div>
      <footer className="legacy-footer">
        <span>↑ ↓ select / history · Tab complete word</span>
        <span>Esc minimize</span>
      </footer>
    </section>
  );
}
